import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CellClickEvent, GridComponent, SelectableMode, SelectableSettings } from '@progress/kendo-angular-grid';
import { Subscription } from 'rxjs';
import { RaciNavigationService } from '../../../../navigation/raci-navigation.service';
import { RaciService } from '../../../raci/services/raci.service';
import { GroupApprovalData } from '../../../raci/models/group-approval-data';
import { ActionStageEnum } from 'src/app/modules/raci/models/enums/action-stage-enum';
import { EventBusService } from '../../../../services/event-bus.service';
import { AppStorageService } from '../../../../services/app-storage.service';
import { ImportService } from '../../../project-valuation/services/import.service';

/**
 * Handles the argus approval matrix component
 */
@Component({
  selector: 'argus-approval-matrix',
  templateUrl: './argus-approval-matrix.component.html',
  styleUrls: ['./argus-approval-matrix.component.css']
})
export class ArgusApprovalMatrixComponent implements OnInit, OnDestroy {
  _/**
 * Indicates whether syncing is allowed.
 */
  public _allowSync: boolean;

  /**
   * Setter for allowSync input property.
   */
  @Input() set allowSync(value: any) {
    this._allowSync = value;
  }

  /**
   * Setter for currentTask input property.
   */
  @Input() set currentTask(data: any) {
    if (data) {
      this.activeTask = data;
    }
  }

  /**
   * The active task.
   */
  activeTask: any;

  /**
   * Indicates whether to show the title info.
   */
  @Input() showTitleInfo = false;

  /**
   * The property to group by.
   */
  @Input() groupBy: string;

  /**
   * The ID of the active portfolio.
   */
  @Input() activePortfolioId: any;

  /**
   * The ID of the active task.
   */
  @Input() activeTaskId: any;

  /**
   * The ID of the active step.
   */
  @Input() activeStepId: any;

  /**
   * The ID of the active project.
   */
  @Input() activeProjectId: any;

  /**
   * The current user.
   */
  @Input() currentUser;

  /**
   * Setter for data input property.
   */
  @Input() set data(value: any) {
    if (value) {
      this.resetSelections();
      this.properties = value.filter(p => p.argusPropAssetId !== null);
      this.filterPropertiesByDataStatus();
      this.nonArgusProperties = value.filter(p => p.argusPropAssetId === null);
      this.properties.forEach(prop => {
        prop.isSelected = false;
      });
    }
  }

  /**
   * Event emitter for updating the approval matrix.
   */
  @Output() onUpdateApprovalMatrix = new EventEmitter<any[]>();

  /**
   * Event emitter for updating the complete task status.
   */
  @Output() updateCompleteTaskStatus = new EventEmitter<boolean>();

  /**
   * Event emitter for showing/hiding a dialog.
   */
  @Output() shouldShowDialog = new EventEmitter<boolean>();

  /**
   * Event emitter for refreshing the user's task list.
   */
  @Output() refreshUserTaskList = new EventEmitter<boolean>();

  /**
   * Event emitter for updating the active task.
   */
  @Output() updateActiveTask = new EventEmitter<any>();

  /**
   * Event emitter for syncing with Argus.
   */
  @Output() onSyncWithArgus = new EventEmitter<[any, any]>();


  /**
   * Array of dirty properties.
   */
  dirtyProperties = new Array<any>();

  /**
   * Array of properties.
   */
  properties: any[];

  /**
   * Array of non-Argus properties.
   */
  nonArgusProperties: any[];

  /**
   * Reference to the current grid component.
   */
  currentGridRef: GridComponent;

  /**
   * Indicates if project approval is selected.
   */
  isProjectApprovalSelected: boolean;

  /**
   * The selected group.
   */
  selectedGroup: string;

  /**
   * The project.
   */
  project: any;

  /**
   * Valuation reports.
   */
  valuationReports: any;

  /**
   * The selected report ID.
   */
  selectedReportId: any;

  /**
   * Array of selected properties for sync.
   */
  selectedProeprtiesForSync: Array<any> = [];

  /**
   * Indicates if a project is selected.
   */
  isProjectSelected = false;

  /**
   * Selectable settings.
   */
  selectableSettings: SelectableSettings;

  /**
   * The mode for selecting items.
   */
  mode: SelectableMode = "single";

  /**
   * Indicates whether dragging is enabled.
   */
  drag = false;

  /**
   * Indicates whether the approval dialog is displayed.
   */
  isApprovalDialogDisplayed = false;

  /**
   * Indicates whether the approve action is triggered.
   */
  isApproveTriggered = false;

  /**
   * Array of properties grouped by sector.
   */
  propertiesBySector: Array<GroupApprovalData> = [];

  /**
   * Array of properties grouped by region.
   */
  propertiesByRegion: Array<GroupApprovalData> = [];

  /**
   * Data for the group.
   */
  groupData: any;

  /**
   * Array of selected report IDs.
   */
  selectedReportIds: any[] = [];

  /**
   * The project ID.
   */
  projectId: number;

  /**
   * Array of subscriptions.
   */
  private _subscriptions = new Array<Subscription>();

  /**
   * Constructor
   * @param _raciNavigationService
   * @param appStorage
   * @param eventBus
   * @param _raciService
   * @param _argusImportService
   */
  constructor(private _raciNavigationService: RaciNavigationService,
    private appStorage: AppStorageService,
    private eventBus: EventBusService,
    private _raciService: RaciService,
    private _argusImportService: ImportService) {
    this.setSelectableSettings();
  }

  /**
   * OnInit
   */
  ngOnInit(): void {
    const portfolio = this.appStorage.getSelectedPortfolio();
    this.projectId = portfolio?.ProjectID;
    this.subscribeToSyncProeprtyEvent();
    this.activePortfolioId = this.activeTask?.taskValuationPortfolioId ?? this.activeTask.portfolioId;
    this.activeProjectId = this.projectId ?? this.activeTask.projectId;
    this.getReports();

    this._subscriptions.push(this._raciNavigationService._openReport.subscribe(data => {
      if (data) {
        this.createReportLink();
      }
    }));

    this.eventBus.subscribeEvent("getDataFromArgusDialog", () => {
      this.onSyncWithArgus.emit([this.selectedReportIds, this.selectedProeprtiesForSync]);
    });
  }

  /**
  * Action stage enum getter
  */
  get ActionStageEnum() {
    return ActionStageEnum;
  }

  /**
   * On destroy
   */
  ngOnDestroy(): void {
    this._subscriptions.forEach(sub => sub.unsubscribe());
    this._raciNavigationService.showSyncWithArgus(false);
  }

  /**
   * Filter participants by data status
   */
  filterPropertiesByDataStatus() {
    this.properties = this.properties.filter(property => {
      if (this.properties.filter(item => item.propertyId == property.propertyId).length > 1) {
        return this.properties.find(item => item.propertyId == property.propertyId && property.dataStatus == "SUCCESS") ??
          this.properties.find(item => item.propertyId == property.propertyId && property.dataStatus == "ERROR");
      } else {
        return property;
      }
    });
  }

  /**
  * On changes actions
  */
  ngOnChanges() {
    if (this.properties) {
      this.groupProperties();
    }
  }

  /**
  * Set Selectable Settings
  */
  public setSelectableSettings(): void {
    this.selectableSettings = {
      mode: this.mode,
      drag: this.drag,
    };
  }

  /**
   * Create report link
   */
  createReportLink() {
    if (this.selectedReportId) {
      const absUrl = window.location.href;
      let url = absUrl.split("#")[0];
      url += "#/rd";
      url += "?ViewReportId=" + this.selectedReportId;
      window.open(url, '_blank');
    }
  }

  /**
   * Get Reports of valuation event
   */
  getReports() {
    this._subscriptions.push(this._raciService.getReports(this.activeProjectId, this.activePortfolioId).subscribe(data => {
      if (data) {
        this.valuationReports = data;
      }
    }));
  }

  /**
   *  Retieves all grid rows
   */
  public getAllRows(): Array<HTMLTableElement> {
    return Array.from(document.querySelectorAll(".properties tr"));
  }

  /**
  * Selection change event for grouped data
  * @param selectedGroup Selected group
  */
  onSelectionChange(selectedGroup: string) {
    this.groupData.forEach(group => {
      if (group.group != selectedGroup) {
        group.selectedRows = [];
      }
    });
  }

  /**
   * On approval selection event
   * @param cell Selected kendo grid cell
   */
  public onRowSelection(cell: CellClickEvent) {
    const report = this.valuationReports?.find(report => { return report.PropertyId == cell.dataItem.propertyId })
    if (report) {
      this._raciNavigationService.onSelectRow(true);
      this.selectedReportId = report.Oid;
    }
  }

  /**
   * Resets grid selections
   */
  private resetSelections() {
    this.isProjectApprovalSelected = false;
    this.dirtyProperties = [];
    this.selectedProeprtiesForSync = [];
    this.isProjectSelected = false;
    this.shouldShowHideSyncIcon();
  }

  /**
   * Subscribes to save approval event
   */
  private subscribeToSyncProeprtyEvent() {
    this._subscriptions.push(this._raciNavigationService._onSaveApproval.subscribe(data => {
      if (data) {
        this.dirtyProperties = [];
        this.updateActiveTask.emit(this.activeTask);
      }
    }));
  }

  /**
  * On propery select
  * @param selectedItem Selected item
  * @param groupState Is data grouped
  */
  onPropertySelect(selectedItem: any) {
    selectedItem.isSelected = !selectedItem.isSelected;
    if (selectedItem.isSelected) {
      this.selectedProeprtiesForSync.push(selectedItem);
      const reportId = this.valuationReports?.find(report => { return report.PropertyId == selectedItem.propertyId })?.Oid;
      if (reportId) {
        this.selectedReportIds.push(reportId);
      }
    } else {
      this.removeFromSelectedProeprties(selectedItem);
    }
    this.shouldShowHideSyncIcon();
    this.isProjectSelected = this.selectedProeprtiesForSync.length == this.properties.length;
  }

  /**
   * Check is item selecteed
   * @param item Item
   */
  isSelected(item: any): boolean {
    return this.selectedProeprtiesForSync.find(approval => approval.propertyId == item.propertyId);
  }

  /**
   * Select all properies
   */
  selectAllProperties() {
    this.isProjectSelected = !this.isProjectSelected;
    if (this.isProjectSelected) {
      this.selectedProeprtiesForSync = [];
      this.properties.forEach(property => {
        property.isSelected = true;
        this.selectedProeprtiesForSync.push(property);
        const reportId = this.valuationReports?.find(report => { return report.PropertyId == property.propertyId })?.Oid;
        if (reportId) {
          this.selectedReportIds.push(reportId);
        }
      });
    } else {
      this.selectedProeprtiesForSync = [];
      this.selectedReportIds = [];
      this.properties.forEach(property => {
        property.isSelected = false;
      });
    }
    this.shouldShowHideSyncIcon();
  }

  /**
   * Should display sellect all checkbox
   */
  sholudDisplaySellectAll() {
    return this.selectedProeprtiesForSync.length;
  }

  /**
   * Check if approved icon appear
   */
  shouldShowHideSyncIcon() {
    if (this.selectedProeprtiesForSync.length > 0
      && (this.activeTask.taskActionStage === ActionStageEnum.Waiting || !this.activeTask.taskActionStage)) {
      this._raciNavigationService.showSyncWithArgus(true);
    } else {
      this._raciNavigationService.showSyncWithArgus(false);
    }
  }

  /**
   * Add to selected approvals list
   * @param item
   */
  addToSelectedProperties(item) {
    item.isSelected = true;
    this.selectedProeprtiesForSync.push(item)
  }

  /**
   * Remove from selected appovals list
   * @param item
   */
  removeFromSelectedProeprties(item) {
    const index = this.selectedProeprtiesForSync.findIndex(approval => approval.propertyId == item.propertyId);
    this.selectedProeprtiesForSync.splice(index, 1);
    this.isProjectSelected = false;

    const reportIdIndex = this.selectedReportIds.findIndex(reportId => reportId == item.propertyId);
    this.selectedReportIds.splice(reportIdIndex, 1);
  }

  /**
 * Checks for the most commpon approver
 * @param approvalList Approvals list
 * @param approvalIndex Approval index (eg. 1,2,3)
 * @param approvalA First approval for comparison
 * @param approvalB Second approval for comparison
 */
  private getMostCommonApprover(approvalList: any[], approvalIndex: number, approvalA: any, approvalB: any) {
    return approvalList.filter(v => v[`approval${approvalIndex}`] === approvalB[`approval${approvalIndex}`]).length - approvalList.filter(v => v[`approval${approvalIndex}`] === approvalA[`approval${approvalIndex}`]).length;
  }

  /**
   * Sets most common approvers for specified group
   * @param group Group object
   */
  private setMostCommonApprover(group: any) {
    for (let approvalIndex = 1; approvalIndex <= 3; approvalIndex++) {
      const mostCommonApprover = group.tasks.sort((approvalA, approvalB) => this.getMostCommonApprover(group.tasks, approvalIndex, approvalA, approvalB))[0];
      const pendingApproval = group.tasks.find(x => !x[`dateApproved${approvalIndex}`] && !x[`dateRejected${approvalIndex}`]);
      group.approvals[0][`approval${approvalIndex}`] = mostCommonApprover[`approval${approvalIndex}`];
      group.approvals[0][`approvalUser${approvalIndex}`] = mostCommonApprover[`approvalUser${approvalIndex}`];
      group.approvals[0][`dateRejected${approvalIndex}`] = !mostCommonApprover[`dateRejected${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateRejected${approvalIndex}`]);
      group.approvals[0][`dateApproved${approvalIndex}`] = !mostCommonApprover[`dateApproved${approvalIndex}`] || pendingApproval ? null : new Date(mostCommonApprover[`dateApproved${approvalIndex}`]);
    }
  }

  /**
 * Check if the group list contains a gorup without name and sets it name to "Unknown"
 * @param groupList Group list
 */
  private checkForUnknownGroup(groupList: any[]) {
    const unknownSector = groupList.find(item => { return !item.group });
    if (unknownSector) {
      unknownSector.group = "Unknown";
    }
  }

  /**
   * Groups properties by sector
   */
  private groupBySector() {
    this.propertiesBySector = [];
    this.properties.forEach(property => {
      if (!this.propertiesBySector.find(item => { return item.group == property.sector })) {
        this.propertiesBySector.push(new GroupApprovalData({ "group": property.sector }));
      }
      this.propertiesBySector.find(item => { return item.group == property.sector }).tasks.push(property);
    });

    this.propertiesBySector.forEach(sector => {
      this.setMostCommonApprover(sector);
    });

    this.checkForUnknownGroup(this.propertiesBySector);
  }

  /**
 * Groups properties by region
 */
  private groupByRegion() {
    this.propertiesByRegion = [];
    this.properties.forEach(property => {
      if (!this.propertiesByRegion.find(item => { return item.group == property.region })) {
        this.propertiesByRegion.push(new GroupApprovalData({ "group": property.region }));
      }
      this.propertiesByRegion.find(item => { return item.group == property.region }).tasks.push(property);
    });

    this.propertiesByRegion.forEach(region => {
      this.setMostCommonApprover(region);
    });

    this.checkForUnknownGroup(this.propertiesByRegion);
  }

  /**
 * Group proerties by sector or region.
 */
  private groupProperties() {
    this.groupBySector();
    this.groupByRegion();
    this.groupData = this.groupBy == 'Sector' ? this.propertiesBySector : this.propertiesByRegion;
    this._raciNavigationService.onSelectRow(false);
  }

  /**
  * Getting and parsing the logs from argus for the last sync
  * @param dataItem
  */
  getLastSyncLogs(dataItem) {
    let contentDialog = '';

    const reportId = this.valuationReports.find(report => {
      return report.PropertyId === dataItem.propertyId;
    }).Oid;

    this._argusImportService.getArgusLogs(reportId).subscribe(logs => {
      logs.forEach(logsContent => {
        contentDialog += logsContent.Message + ' <br>';
      });

      this.eventBus.broadcastEvent("argusDialog", {
        success: () => {
          // Nothing
        },
        cancel: () => {
          // Nothing
        },
        dialogContent: () => {
          return contentDialog === "" ? "No available logs." : contentDialog;
        },
        shouldShowCloseButton: () => {
          return true;
        }
      }, 'exportToArgusConfirmDialog');
    });
  }
}
